import { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Paperclip, FileText, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 10 * 1024 * 1024;

function humanSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function ChatBox({ projectId, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const { data } = await api.get(`/messages/${projectId}`);
      setMessages(data);
    } catch { /* ignore */ }
  };

  const markRead = async () => {
    try { await api.post(`/messages/${projectId}/read`); } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchMessages();
    markRead();
    const t = setInterval(() => { fetchMessages(); markRead(); }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [projectId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      await api.post("/messages", { project_id: projectId, text });
      setText("");
      fetchMessages();
    } catch { toast.error("No se pudo enviar"); }
    finally { setLoading(false); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error(`Máximo 10 MB. El archivo pesa ${humanSize(f.size)}.`);
      e.target.value = "";
      return;
    }
    uploadFile(f);
    e.target.value = "";
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (text.trim()) fd.append("text", text.trim());
      await api.post(`/messages/${projectId}/attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setText("");
      toast.success("Archivo enviado");
      fetchMessages();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error subiendo archivo");
    } finally { setUploading(false); }
  };

  const downloadUrl = (msg) => {
    const token = localStorage.getItem("tasklink_token") || "";
    return `${API}/messages/${projectId}/attachment/${msg.attachment.attachment_id}?auth=${encodeURIComponent(token)}`;
  };

  return (
    <div className="border border-border rounded-lg bg-card flex flex-col h-[520px]" data-testid="chat-box">
      <div className="px-5 py-3 border-b border-border">
        <div className="text-xs tracking-widest uppercase font-bold text-muted-foreground">Chat del Proyecto</div>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-sm text-muted-foreground pt-10">
            Aún no hay mensajes. Inicia la conversación.
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === currentUserId;
          const att = m.attachment;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm ${
                mine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
              }`}>
                {!mine && (
                  <div className="text-[11px] font-semibold mb-0.5 opacity-80 flex items-center gap-1">
                    <span>{m.sender_name}</span>
                    {m.sender_role === "company" && (
                      <span className="text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 rounded-sm bg-primary/15 text-primary">
                        Empresa
                      </span>
                    )}
                  </div>
                )}
                {m.text && <div className="whitespace-pre-line">{m.text}</div>}
                {att && (
                  <a
                    href={downloadUrl(m)}
                    download={att.original_filename}
                    className={`mt-2 flex items-center gap-3 rounded-md p-2.5 border ${
                      mine ? "bg-primary-foreground/10 border-primary-foreground/20 hover:bg-primary-foreground/20"
                           : "bg-background border-border hover:bg-accent"
                    } transition-colors`}
                    data-testid={`attachment-${att.attachment_id}`}
                  >
                    <FileText className="w-5 h-5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium truncate">{att.original_filename}</div>
                      <div className="text-[10px] opacity-70">{humanSize(att.size)}</div>
                    </div>
                    <Download className="w-4 h-4 shrink-0" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-border flex gap-2 items-end">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={onPickFile}
          data-testid="chat-file-input"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-[42px] w-[42px] shrink-0"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="chat-attach-btn"
          title="Adjuntar archivo (máx 10 MB)"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
        </Button>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Escribe un mensaje o adjunta un archivo…"
          rows={1}
          className="resize-none min-h-[42px]"
          data-testid="chat-input"
        />
        <Button
          onClick={send}
          disabled={loading || uploading}
          size="icon"
          className="rounded-full h-[42px] w-[42px] shrink-0"
          data-testid="chat-send-btn"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
